import React, { useState, useEffect } from 'react';
import ProductCard from './ProductCard';
import Sidebar from './Sidebar';
import { useCategory } from '../context/CategoryContext';
import SEO from './SEO';
import API_BASE_URL from '../api/Config';
import './Shop.css';

const Shop = () => {
  const { categories } = useCategory();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({ categories: [], subcategories: [] });

  // Fetch all products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/products/all`, {
          headers: {
            'Accept': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setProducts(data.products || []);
        setFilteredProducts(data.products || []);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Filter products when filters change
  useEffect(() => {
    if (selectedFilters.categories.length === 0 && selectedFilters.subcategories.length === 0) {
      // No filters applied, show all products
      setFilteredProducts(products);
    } else {
      // Apply filters
      const filtered = products.filter((product) => {
        const productCategoryIds = product.category_ids?.map(cat => parseInt(cat.id)) || [];
        
        // Check if product matches selected categories
        const matchesCategory = selectedFilters.categories.length === 0 || 
          selectedFilters.categories.some(catId => productCategoryIds.includes(catId));
        
        // Check if product matches selected subcategories
        const matchesSubcategory = selectedFilters.subcategories.length === 0 || 
          selectedFilters.subcategories.some(subId => productCategoryIds.includes(parseInt(subId.split('-')[0])));
        
        return matchesCategory && matchesSubcategory;
      });
      
      setFilteredProducts(filtered);
    }
  }, [selectedFilters, products]);

  const handleWishlistToggle = (product) => {
    console.log('Wishlist toggled for:', product.name);
  };

  const handleFilterChange = (filters) => {
    setSelectedFilters(filters);
  };

  // Format price with KSh
  const formatPrice = (price) => {
    return `KSh ${price}`;
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Shop - Techie Interiors",
    "description": "Browse our complete collection of interior design products and furniture in Nairobi Kenya",
    "url": "https://techieinteriors.co.ke/shop",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Techie Interiors",
      "url": "https://techieinteriors.co.ke"
    }
  };

  return (
    <div className="shop-container">
      {/* SEO Component */}
      <SEO
        title="Shop Interior Design Products Online in Nairobi, Kenya | Techie Interiors"
        description="Shop high-quality interior design products online in Kenya. Browse gypsum ceilings, wardrobes, kitchen cabinets, TV wall units, lighting, and home décor with delivery across Kenya."
        image="/techLogo.jpeg"
        url="https://techieinteriors.co.ke/shop"
        structuredData={structuredData}
        noindex={true}
      />
      <div className="shop-layout">
        <Sidebar onFilterChange={handleFilterChange} />
        
        <div className="products-grid">
          {loading ? (
            <div className="shop-loading">
              <div className="loading-spinner"></div>
              <p>Loading products...</p>
            </div>
          ) : error ? (
            <div className="shop-error">
              <p>Error loading products. Please try again later.</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="no-products-found">
              <div className="no-products-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <h3>No Products Found</h3>
              <p>
                {selectedFilters.categories.length > 0 || selectedFilters.subcategories.length > 0
                  ? 'No products match your selected filters. Try adjusting your filters or browse all products.'
                  : 'No products are currently available. Please check back later.'}
              </p>
              {(selectedFilters.categories.length > 0 || selectedFilters.subcategories.length > 0) && (
                <button 
                  className="clear-filters-btn"
                  onClick={() => setSelectedFilters({ categories: [], subcategories: [] })}
                >
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            filteredProducts.map((product) => {
              const discountedPrice = product.discount > 0 
                ? product.price * (1 - product.discount / 100)
                : product.price;
              
              return (
              <ProductCard 
                key={product.id} 
                product={{
                  id: product.id,
                  name: product.name,
                  description: product.description?.replace(/<[^>]*>/g, '').substring(0, 100) + '...',
                  price: formatPrice(discountedPrice.toFixed(2)),
                  originalPrice: product.discount > 0 ? formatPrice(product.price) : null,
                  discount: product.discount || 0,
                  image: product.image_fullpath?.[0] || product.image?.[0] || 'https://via.placeholder.com/400x300',
                  category: categories.find(c => product.category_ids?.some(pc => parseInt(pc.id) === c.id))?.name || 'Uncategorized'
                }}
                onWishlistToggle={handleWishlistToggle}
              />
            )})
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
